import { useMemo } from "react";
import { Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Ban, CalendarCheck, Download, KeyRound, Loader2, LogIn, LogOut, MapPin, Minus, Plus, Route as RouteIcon, ShieldCheck, Trash2, Upload, UserCog, UserX, Warehouse } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { Tabs, type TabItem } from "@/components/ui/Tabs";
import { listActivity, type ActivityKind, type ActivityRow } from "@/services/activityLog";

const TABS: TabItem[] = [
  { to: "/activity", label: "Sign-In History", end: true },
  { to: "/activity/uploads", label: "Upload History" },
  // Tab covers route lifecycle events + CSV exports (when the latter ships).
  // Kept the URL slug as "/exports" so any existing bookmarks still resolve.
  { to: "/activity/exports", label: "Route & Export History" },
];

export function ActivityLayout() {
  return (
    <div>
      <PageHeader
        title="Activity"
        description="Audit trail for sign-ins, imports, and exports across the team."
      />
      <Tabs items={TABS} />
      <div className="px-4 py-4 sm:px-8 sm:py-6">
        <Outlet />
      </div>
    </div>
  );
}

// ---------- Per-tab pages: each calls ActivityTable with a kinds filter ------

export function ActivitySignIns() {
  return (
    <ActivityTable
      kinds={["sign_in", "sign_out"]}
      emptyTitle="No sign-in events yet"
      emptyBody="Sign-ins and sign-outs across the team will appear here as they happen."
    />
  );
}

export function ActivityUploads() {
  return (
    <ActivityTable
      kinds={["lead_import"]}
      emptyTitle="No CSV uploads yet"
      emptyBody="Each lead-import run shows the file name, row counts, and uploader."
    />
  );
}

export function ActivityExports() {
  return (
    <ActivityTable
      // Bundles route lifecycle + exports + destructive/security events so
      // admins can answer "what changed?" from one tab. Sign-Ins stays pure
      // authentication.
      kinds={[
        "lead_export", "lead_delete", "lead_assign",
        "route_create", "route_cancel", "route_delete", "route_reassign", "route_stop_add", "route_stop_remove",
        "member_delete", "member_role_change", "member_active_change",
        "zone_create", "zone_delete", "zone_assign",
        "depot_create", "depot_delete",
        "password_reset_requested", "password_changed",
      ]}
      emptyTitle="No changes recorded yet"
      emptyBody="Deletes, role changes, zone/depot edits, route lifecycle, CSV exports, and password events show up here."
    />
  );
}

// ---------- Shared table -----------------------------------------------------

interface ActivityTableProps {
  kinds: ActivityKind[];
  emptyTitle: string;
  emptyBody: string;
}

function ActivityTable({ kinds, emptyTitle, emptyBody }: ActivityTableProps) {
  const cacheKey = useMemo(() => [...kinds].sort().join(","), [kinds]);
  const query = useQuery({
    queryKey: ["activity", cacheKey],
    queryFn: () => listActivity({ kinds, limit: 200 }),
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading activity…
      </div>
    );
  }

  if (query.error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
        Couldn't load activity: {(query.error as Error).message}
      </div>
    );
  }

  const rows = query.data ?? [];
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
        <p className="text-sm font-medium text-slate-700">{emptyTitle}</p>
        <p className="mt-1 text-xs text-slate-500">{emptyBody}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2.5 text-left">When</th>
            <th className="px-4 py-2.5 text-left">Who</th>
            <th className="px-4 py-2.5 text-left">Event</th>
            <th className="px-4 py-2.5 text-left">Detail</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <ActivityRowView key={r.id} row={r} />
          ))}
        </tbody>
      </table>
      </div>
      <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500">
        Showing {rows.length} most recent {rows.length === 1 ? "event" : "events"}.
      </div>
    </div>
  );
}

function ActivityRowView({ row }: { row: ActivityRow }) {
  const dt = parseISO(row.created_at);
  return (
    <tr className="hover:bg-slate-50">
      <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
        <div>{format(dt, "MMM d, yyyy")}</div>
        <div className="text-xs text-slate-500">{format(dt, "h:mm a")}</div>
      </td>
      <td className="px-4 py-2.5 text-slate-700">
        <div className="font-medium text-slate-900">{row.actor_name ?? "(removed user)"}</div>
        {row.actor_email && <div className="text-xs text-slate-500">{row.actor_email}</div>}
      </td>
      <td className="px-4 py-2.5">
        <KindBadge kind={row.kind} />
      </td>
      <td className="px-4 py-2.5 text-slate-700">{row.summary || <span className="text-slate-400">—</span>}</td>
    </tr>
  );
}

// Brand-aligned palette: brand-tinted badges for "positive" actions (in-app
// activity), neutral slate for terminations (sign-out, route cancel/delete).
// Earlier iterations used 7 distinct colours per event type — broke brand
// cohesion and added visual noise without information value (the icon +
// label already disambiguate). Two tints, one accent.
const KIND_META: Record<ActivityKind, { label: string; cls: string; Icon: typeof LogIn }> = {
  sign_in:      { label: "Sign-in",         cls: "bg-brand-50 text-brand-700",  Icon: LogIn },
  sign_out:     { label: "Sign-out",        cls: "bg-slate-100 text-slate-600", Icon: LogOut },
  lead_import:  { label: "Import",          cls: "bg-brand-50 text-brand-700",  Icon: Upload },
  lead_export:  { label: "Export",          cls: "bg-brand-50 text-brand-700",  Icon: Download },
  route_create: { label: "Route created",   cls: "bg-brand-50 text-brand-700",  Icon: RouteIcon },
  route_cancel: { label: "Route cancelled", cls: "bg-slate-100 text-slate-600", Icon: Ban },
  // Deletion is the one event that warrants a destructive accent — admins
  // scanning the log for "what changed?" should be able to spot a hard delete
  // at a glance. Kept rose-tinted for that one reason.
  route_delete: { label: "Route deleted",   cls: "bg-rose-50 text-rose-700",    Icon: Trash2 },
  password_reset_requested: { label: "Password reset", cls: "bg-brand-50 text-brand-700", Icon: KeyRound },
  // Phase-3 audit expansion (migration 0036): every mutating surface logs.
  lead_delete:          { label: "Leads deleted",    cls: "bg-rose-50 text-rose-700",    Icon: Trash2 },
  lead_assign:          { label: "Leads assigned",   cls: "bg-brand-50 text-brand-700",  Icon: UserCog },
  route_reassign:       { label: "Route reassigned", cls: "bg-brand-50 text-brand-700",  Icon: RouteIcon },
  route_stop_add:       { label: "Stop added",       cls: "bg-brand-50 text-brand-700",  Icon: Plus },
  route_stop_remove:    { label: "Stop removed",     cls: "bg-slate-100 text-slate-600", Icon: Minus },
  member_delete:        { label: "Member deleted",   cls: "bg-rose-50 text-rose-700",    Icon: UserX },
  member_role_change:   { label: "Role changed",     cls: "bg-brand-50 text-brand-700",  Icon: ShieldCheck },
  member_active_change: { label: "Account toggled",  cls: "bg-slate-100 text-slate-600", Icon: UserCog },
  zone_create:          { label: "Zone created",     cls: "bg-brand-50 text-brand-700",  Icon: MapPin },
  zone_delete:          { label: "Zone deleted",     cls: "bg-rose-50 text-rose-700",    Icon: Trash2 },
  zone_assign:          { label: "Zone assigned",    cls: "bg-brand-50 text-brand-700",  Icon: MapPin },
  depot_create:         { label: "Depot created",    cls: "bg-brand-50 text-brand-700",  Icon: Warehouse },
  depot_delete:         { label: "Depot deleted",    cls: "bg-rose-50 text-rose-700",    Icon: Trash2 },
  appointment_set:      { label: "Appointment set",  cls: "bg-brand-50 text-brand-700",  Icon: CalendarCheck },
  appointment_status_change: { label: "Appointment updated", cls: "bg-slate-100 text-slate-600", Icon: CalendarCheck },
  password_changed:     { label: "Password changed", cls: "bg-brand-50 text-brand-700",  Icon: KeyRound },
};

// Unknown kinds (e.g. enum extended before the client redeployed) render a
// neutral badge instead of crashing on meta.Icon.
const KIND_FALLBACK = { label: "Activity", cls: "bg-slate-100 text-slate-600", Icon: LogIn };

function KindBadge({ kind }: { kind: ActivityKind }) {
  const meta = KIND_META[kind] ?? KIND_FALLBACK;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}
